import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { GraphicEq } from '@mui/icons-material';
import { GET_PROJECT_PREVIEW } from '@/graphql/projects';
import {
  type GetProjectPreviewQueryData,
  type GetProjectPreviewQueryVariables,
} from '@/generated/graphql';
import { SectionLabel } from '@/common/components/ui';
import { useProjectDetails } from '../context/ProjectDetailsContext';
import { useTrackComments } from '@/hooks/useTrackComments';
import { WaveformCommentsDialog } from '@/common/components/waveformComments/WaveformCommentsDialog';
import { AudioPlayer } from './AudioPlayer';

/** Version-keyed instance prevents old audio surviving a version switch. */
export default function ProjectPreviewBanner() {
  const { selectedVersion } = useProjectDetails();
  return <VersionPreview key={selectedVersion} />;
}

function VersionPreview() {
  const { projectId, project, selectedVersion } = useProjectDetails();
  const { data, loading, error, refetch } = useQuery<
    GetProjectPreviewQueryData,
    GetProjectPreviewQueryVariables
  >(GET_PROJECT_PREVIEW, {
    variables: { projectId, version: selectedVersion },
    // ProjectType shares an ID across versions. Do not let a historical
    // preview overwrite the library's latest preview in Apollo's entity cache.
    fetchPolicy: 'no-cache',
    skip: !project?.versionHistory.some((v) => v.version === selectedVersion),
  });
  const preview = data?.projectVersion?.previewFile;
  const [commentsOpen, setCommentsOpen] = useState(false);
  const comments = useTrackComments({
    projectId,
    versionNumber: selectedVersion,
    skip: !preview,
  });
  const versionThreads = useMemo(
    () => comments.threads.filter((t) => t.versionNumber === selectedVersion),
    [comments.threads, selectedVersion]
  );
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [initialTimeMs, setInitialTimeMs] = useState(0);

  return (
    <Box sx={{ pt: 3, pb: 1 }}>
      <Stack
        direction="row"
        gap={1.5}
        alignItems="baseline"
        flexWrap="wrap"
        sx={{ mb: 1 }}
      >
        <SectionLabel>Preview · Version {selectedVersion}</SectionLabel>
        {preview?.waveForm && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ overflowWrap: 'anywhere' }}
          >
            {preview.fileName}
          </Typography>
        )}
      </Stack>
      <Box sx={{ minHeight: 244 }} aria-busy={loading}>
        {loading ? (
          <Skeleton variant="rectangular" height={244} />
        ) : error ? (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                onClick={() => {
                  void refetch();
                }}
              >
                Retry
              </Button>
            }
          >
            Could not load the preview for version {selectedVersion}.
          </Alert>
        ) : preview?.waveForm ? (
          <>
            <AudioPlayer
              key={`${selectedVersion}-${preview._id}`}
              variant="banner"
              projectId={projectId}
              fileId={preview._id}
              waveForm={preview.waveForm}
              commentThreads={versionThreads}
              onOpenComments={(id, atMs) => {
                setHighlightId(id ?? null);
                setInitialTimeMs(atMs ?? 0);
                setCommentsOpen(true);
              }}
              label={`version ${selectedVersion} preview for ${project?.name || 'project'}`}
            />
            {comments.error && (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                data-cy="banner-comments-error"
                sx={{ color: 'text.secondary' }}
              >
                <Typography variant="body2">Couldn't load comments</Typography>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => void comments.refetch()}
                >
                  Retry comments
                </Button>
              </Stack>
            )}
          </>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{ minHeight: 244, color: 'text.secondary' }}
          >
            <GraphicEq />
            <Typography variant="body2">
              No audio preview for version {selectedVersion} yet.
            </Typography>
          </Stack>
        )}
      </Box>
      {commentsOpen && (
        <WaveformCommentsDialog
          open
          projectId={projectId}
          versionNumber={selectedVersion}
          preview={preview ?? null}
          initialHighlightId={highlightId}
          initialTimeMs={initialTimeMs}
          onClose={() => setCommentsOpen(false)}
        />
      )}
    </Box>
  );
}
